/**
 * El puente hacia Sentry: la app le habla a ESTO, nunca a `@sentry/react` directo.
 *
 * 🚨 POR QUÉ EXISTE. Medido sobre el sourcemap del bundle: Sentry pesa 935 KB de fuente
 *    (`@sentry/core` 394 · `replay` 300 · `browser-utils` 134 · `browser` 107) y viajaba DENTRO
 *    del chunk de entrada. O sea que todo el que abría el panel descargaba, interpretaba y
 *    EJECUTABA casi un megabyte de monitoreo antes de ver la primera fila de su lista.
 *
 *    Y no se podía sacar sin esto: `logger.ts` importa Sentry, y `logger` lo usa medio panel. Un
 *    solo import estático en un módulo compartido ancla la librería entera al arranque — que es el
 *    mismo mecanismo por el que el editor de texto también estaba ahí.
 *
 * ⭐ AHORA SENTRY LLEGA DESPUÉS DEL PRIMER PINTADO, y lo que pase mientras tanto NO SE PIERDE: se
 *    encola acá y se entrega cuando el módulo carga. Es la diferencia entre diferir y resignar.
 *
 * 🪤 LA COLA ESTÁ ACOTADA. Si Sentry nunca carga (red caída, bloqueador de anuncios), una cola sin
 *    techo crece hasta comerse la memoria de la pestaña — un problema peor que el que vino a
 *    resolver. Se guardan los primeros 50 eventos y el resto se descarta en silencio: son los
 *    primeros los que explican un arranque roto.
 */

type ClienteSentry = typeof import('@sentry/react');

let sentry: ClienteSentry | null = null;

type Pendiente =
  | { tipo: 'excepcion'; error: unknown; extra?: Record<string, unknown> }
  | { tipo: 'miga'; categoria: string; mensaje: string; nivel: 'error' | 'warning' | 'info' }
  | { tipo: 'usuario'; id: string; email?: string; tenantId: string; role: string }
  | { tipo: 'sin-usuario' };

const MAX_PENDIENTES = 50;
const pendientes: Pendiente[] = [];

function encolar(p: Pendiente): void {
  if (pendientes.length >= MAX_PENDIENTES) return;
  pendientes.push(p);
}

/** La llama `instrument.ts` cuando termina de cargar e inicializar. Vacía la cola. */
export function registrarSentry(cliente: ClienteSentry): void {
  sentry = cliente;
  for (const p of pendientes.splice(0)) {
    switch (p.tipo) {
      case 'excepcion':
        cliente.captureException(p.error, p.extra ? { extra: p.extra } : undefined);
        break;
      case 'miga':
        cliente.addBreadcrumb({ category: p.categoria, message: p.mensaje, level: p.nivel });
        break;
      case 'usuario':
        cliente.setUser({ id: p.id, email: p.email });
        cliente.setTag('tenant_id', p.tenantId);
        cliente.setTag('user_role', p.role);
        break;
      case 'sin-usuario':
        cliente.setUser(null);
        break;
    }
  }
}

export function capturarExcepcion(error: unknown, extra?: Record<string, unknown>): void {
  if (sentry) sentry.captureException(error, extra ? { extra } : undefined);
  else encolar({ tipo: 'excepcion', error, extra });
}

export function dejarMiga(categoria: string, mensaje: string, nivel: 'error' | 'warning' | 'info'): void {
  if (sentry) sentry.addBreadcrumb({ category: categoria, message: mensaje, level: nivel });
  else encolar({ tipo: 'miga', categoria, mensaje, nivel });
}

export function identificarUsuario(u: { id: string; email?: string; tenantId: string; role: string }): void {
  if (sentry) {
    sentry.setUser({ id: u.id, email: u.email });
    sentry.setTag('tenant_id', u.tenantId);
    sentry.setTag('user_role', u.role);
  } else encolar({ tipo: 'usuario', ...u });
}

export function olvidarUsuario(): void {
  if (sentry) sentry.setUser(null);
  else encolar({ tipo: 'sin-usuario' });
}

/**
 * Un span de rendimiento, si Sentry ya está; si no, el trabajo se hace igual.
 *
 * 🪤 ACÁ NO SE ENCOLA NADA. Un span mide el tiempo de algo que está pasando AHORA: guardarlo para
 *    reproducirlo después mediría cualquier cosa menos lo que decía medir. Se pierde la traza y se
 *    conserva lo importante, que es ejecutar el trabajo.
 */
export interface SpanMinimo {
  setStatus: (estado: { code: number; message?: string }) => void;
}

export function conSpan<T>(
  nombre: string,
  op: string,
  atributos: Record<string, unknown> | undefined,
  fn: (span?: SpanMinimo) => T,
): T {
  // Sin Sentry, el callback corre igual y recibe `undefined`: el trabajo no depende de la traza.
  if (!sentry) return fn(undefined);
  return sentry.startSpan({ name: nombre, op, attributes: atributos as never }, fn as never) as T;
}
